import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import httpRequest from '../utils/request';
// Usar endpoints relativos para el cliente compartido
const API_URL = '/customers';
// Toggle to show/hide the "Validar CUIT" button and helper hint in the New Customer modal
const SHOW_VALIDATE_CUIT = true;

const Customers = () => {
  const { user } = useSelector((state) => state.auth);
  const serviceType = user?.tenantId?.serviceType || 'full_pos';
  const isEcommerceLite = serviceType === 'ecommerce_lite';

  const navigate = useNavigate();

  const [customers, setCustomers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [formData, setFormData] = useState({
    _id: '',
    razonSocial: '',
    cuit: '',
    tipoContribuyente: '',
    ingresosBrutos: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
    cuentaCorrienteEnabled: false,
    creditLimit: 0,
    paymentDueDays: 0,
  });
  const [validateLoading, setValidateLoading] = useState(false);
  // Validación por padrón AFIP deshabilitada temporalmente (estados removidos para reducir warnings)

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await httpRequest.get(`${API_URL}/all`);
      setCustomers(res.data);
      setFiltered(res.data);
    } catch (err) {
      console.error(err);
      toast.error('❌ Error al cargar clientes');
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    const result = customers.filter(
      (c) =>
        (c.razonSocial || c.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.cuit || '').includes(search) ||
        c.phone.includes(search) ||
        (c.email && c.email.toLowerCase().includes(search.toLowerCase()))
    );
    setFiltered(result);
  }, [search, customers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.razonSocial || !formData.cuit) {
      toast.warning('⚠️ Razón social y CUIT son obligatorios');
      return;
    }

    try {
      if (editMode) {
        await httpRequest.patch(`${API_URL}/update/${formData._id}`, formData);
        toast.success('✅ Cliente actualizado');
      } else {
        const { _id, ...dataToSend } = formData;
        await httpRequest.post(`${API_URL}/create`, dataToSend);
        toast.success('✅ Cliente creado');
      }

      fetchCustomers();
      setShowModal(false);
      setEditMode(false);
      setFormData({ _id: '', razonSocial: '', cuit: '', tipoContribuyente: '', ingresosBrutos: '', phone: '', email: '', address: '', notes: '', cuentaCorrienteEnabled: false, creditLimit: 0, paymentDueDays: 0 });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || '❌ Error al guardar cliente');
    }
  };

  // Validación por padrón AFIP deshabilitada temporalmente.
  // La función que hacía la consulta se ha eliminado para evitar llamadas al servicio externo
  // mientras trabajamos en producción. Se mantienen estados mínimos por compatibilidad.

  const handleEdit = (customer) => {
    setFormData({
      _id: customer._id,
      razonSocial: customer.razonSocial || customer.name || '',
      cuit: customer.cuit || '',
      ingresosBrutos: (customer.ingresosBrutos && customer.ingresosBrutos.numero) || customer.ingresosBrutos || '',
      tipoContribuyente: customer.tipoContribuyente || '',
      phone: customer.phone || '',
      email: customer.email || '',
      address: customer.address || '',
      notes: customer.notes || '',
      cuentaCorrienteEnabled: customer.cuentaCorrienteEnabled || false,
      creditLimit: customer.creditLimit || 0,
      paymentDueDays: customer.paymentDueDays || 0,
    });
    setEditMode(true);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('⚠️ ¿Estás seguro de eliminar este cliente?')) return;

    try {
      await httpRequest.delete(`${API_URL}/delete/${id}`);
      fetchCustomers();
      toast.success('✅ Cliente eliminado');
    } catch (err) {
      console.error(err);
      toast.error('❌ Error al eliminar cliente');
    }
  };

  // Validar CUIT usando credenciales AFIP del tenant. Rellena campos si el servicio devuelve datos.
  const handleValidateCuit = async () => {
    try {
      if (!formData.cuit || String(formData.cuit).trim() === '') {
        toast.warning('⚠️ Ingrese un CUIT para validar');
        return;
      }
      setValidateLoading(true);
      const cuitRaw = String(formData.cuit).trim();

      // Primero intentar con el servicio AFIP usando credenciales del tenant
      try {
        const resp = await httpRequest.get(`${API_URL}/validate-afip/${encodeURIComponent(cuitRaw)}`);
        const body = resp.data;

        if (body && body.ok) {
          const remote = body.data || {};
          const razon = remote.razonSocial || remote.nombre || '';
          const tipo = remote.tipoContribuyente || remote.condicionIVA || '';

          setFormData((prev) => ({
            ...prev,
            razonSocial: razon || prev.razonSocial,
            tipoContribuyente: tipo || prev.tipoContribuyente,
          }));

          toast.success('✅ CUIT validado con padrón AFIP oficial. Datos actualizados.');
          return;
        }
      } catch (afipErr) {
        // Log detallado del error
        console.error('Error validación AFIP:', afipErr);

        const errData = afipErr.response?.data;
        const errMsg = errData?.message || afipErr.message;
        const errHint = errData?.hint;

        // Mostrar mensaje específico según el tipo de error
        if (afipErr.response?.status === 404) {
          toast.warning(`⚠️ ${errMsg}${errHint ? `\n💡 ${errHint}` : ''}`);
          console.info('ℹ️ Intentando con servicio alternativo (ARCA)...');
        } else if (afipErr.response?.status === 400) {
          toast.warning(`⚠️ ${errMsg}${errHint ? `\n💡 ${errHint}` : ''}`);
          console.info('ℹ️ Intentando con servicio alternativo (ARCA)...');
        } else if (afipErr.response?.status === 502) {
          toast.error(`❌ ${errMsg}${errHint ? `\n💡 ${errHint}` : ''}`);
          console.info('ℹ️ Intentando con servicio alternativo (ARCA)...');
        } else {
          toast.warning(`⚠️ Error con AFIP: ${errMsg}`);
          console.info('ℹ️ Intentando con servicio alternativo (ARCA)...');
        }

        // Fallback a servicio ARCA
        try {
          const respArca = await httpRequest.get(`${API_URL}/validate/${encodeURIComponent(cuitRaw)}`);
          const bodyArca = respArca.data;

          if (bodyArca && bodyArca.ok) {
            const remote = bodyArca.data || {};
            const razon = remote.razonSocial || remote.razon_social || remote.nombre || remote.nombreRazon || remote.razon || '';
            const tipo = remote.tipoContribuyente || remote.condicionIVA || remote.tipo || '';
            let iibb = '';
            if (remote.ingresosBrutos) {
              if (typeof remote.ingresosBrutos === 'string') iibb = remote.ingresosBrutos;
              else if (remote.ingresosBrutos.numero) iibb = remote.ingresosBrutos.numero;
            }

            setFormData((prev) => ({
              ...prev,
              razonSocial: razon || prev.razonSocial,
              tipoContribuyente: tipo || prev.tipoContribuyente,
              ingresosBrutos: iibb || prev.ingresosBrutos,
            }));

            toast.success('✅ CUIT validado con servicio alternativo. Datos cargados.');
            return;
          } else {
            const msg = (bodyArca && (bodyArca.message || JSON.stringify(bodyArca))) || 'Respuesta inesperada del servicio de validación';
            toast.error(`❌ Servicio alternativo: ${String(msg).slice(0, 150)}`);
          }
        } catch (arcaErr) {
          console.error('Error servicio ARCA:', arcaErr);
          const arcaMsg = arcaErr.response?.data?.message || arcaErr.message || 'Error desconocido';
          toast.error(`❌ Ambos servicios fallaron. Último error: ${String(arcaMsg).slice(0, 100)}`);
        }
      }
    } catch (err) {
      console.error('validateCuit error general:', err);
      const msg = err.response?.data?.message || err.message || 'Error validando CUIT';
      toast.error(`❌ ${String(msg).slice(0, 200)}`);
    } finally {
      setValidateLoading(false);
    }
  };

  return (
    <div className="container-safe" style={{ padding: '30px', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>👥 Gestión de Clientes</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button style={btnNav} onClick={() => navigate('/')}>🏠 Dashboard</button>
          {!isEcommerceLite && (
            <button style={{ ...btnNav, background: '#28a745' }} onClick={() => navigate('/sales')}>💵 Punto de Venta</button>
          )}
          <button style={{ ...btnNav, background: '#dc3545' }} onClick={() => navigate('/login')}>🚪 Salir</button>
        </div>
      </div>

      <div className="stack-sm" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between' }}>
        <input
          type="text"
          placeholder="🔍 Buscar por razón social, CUIT, teléfono o email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={inputSearch}
        />
        <button
          style={btnGreen}
          onClick={() => {
            setShowModal(true);
            setEditMode(false);
            setFormData({ _id: '', razonSocial: '', cuit: '', tipoContribuyente: '', phone: '', email: '', address: '', notes: '', cuentaCorrienteEnabled: false, creditLimit: 0, paymentDueDays: 0 });
          }}
        >
          ➕ Nuevo Cliente
        </button>
      </div>

      {/* Tabla de clientes */}
      <div className="responsive-table">
        <table style={table}>
          <thead style={{ backgroundColor: '#007bff', color: 'white' }}>
            <tr>
              <th style={th}>Razón Social</th>
              <th style={th}>CUIT</th>
              <th style={th}>IIBB</th>
              <th style={th}>Tipo Contribuyente</th>
              <th style={th}>Teléfono</th>
              <th style={th}>Email</th>
              <th style={th}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                  No hay clientes registrados
                </td>
              </tr>
            ) : (
              filtered.map((customer) => (
                <tr key={customer._id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={td}>{customer.razonSocial || customer.name}</td>
                  <td style={td}>{customer.cuit || '-'}</td>
                  <td style={td}>{(customer.ingresosBrutos && customer.ingresosBrutos.numero) || customer.ingresosBrutos || '-'}</td>
                  <td style={td}>{customer.tipoContribuyente || '-'}</td>
                  <td style={td}>{customer.phone || '-'}</td>
                  <td style={td}>{customer.email || '-'}</td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button style={btnSmallBlue} onClick={() => handleEdit(customer)}>
                        ✏️
                      </button>
                      <button style={btnSmallRed} onClick={() => handleDelete(customer._id)}>
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal de crear/editar */}
      {showModal && (
        <div style={modalOverlay} onClick={() => setShowModal(false)}>
          <div style={modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginBottom: '20px' }}>
              {editMode ? '✏️ Editar Cliente' : '➕ Nuevo Cliente'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '15px' }}>
                <label style={labelStyle}>Razón Social *</label>
                <input
                  type="text"
                  value={formData.razonSocial}
                  onChange={(e) => setFormData({ ...formData, razonSocial: e.target.value })}
                  style={inputModal}
                  placeholder="Razón social / nombre de la empresa"
                  required
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={labelStyle}>CUIT *</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={formData.cuit}
                    onChange={(e) => setFormData({ ...formData, cuit: e.target.value })}
                    style={{ ...inputModal, flex: 1 }}
                    placeholder="XX-XXXXXXXX-X"
                    required
                  />
                  {SHOW_VALIDATE_CUIT && (
                    <button
                      type="button"
                      onClick={handleValidateCuit}
                      disabled={validateLoading}
                      title="Validar CUIT consultando el padrón AFIP con las credenciales del tenant"
                      style={{ padding: '8px 10px', borderRadius: '6px', border: 'none', background: validateLoading ? '#6c757d' : '#17a2b8', color: '#fff', cursor: validateLoading ? 'not-allowed' : 'pointer' }}
                      id="btn-validate-cuit"
                    >
                      {validateLoading ? '⏳ Validando...' : '🔍 Validar CUIT'}
                    </button>
                  )}
                </div>
                {SHOW_VALIDATE_CUIT && (
                  <div style={{ marginTop: '6px', fontSize: '13px' }}>
                    <em style={{ color: '#6c757d' }}>💡 Valida el CUIT contra el padrón de AFIP usando las credenciales fiscales configuradas. Si falla, intenta con servicio alternativo.</em>
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={labelStyle}>IIBB (Ingresos Brutos)</label>
                <input
                  type="text"
                  value={formData.ingresosBrutos}
                  onChange={(e) => setFormData({ ...formData, ingresosBrutos: e.target.value })}
                  style={inputModal}
                  placeholder="Número de IIBB"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={labelStyle}>Tipo de Contribuyente</label>
                <select
                  value={formData.tipoContribuyente}
                  onChange={(e) => setFormData({ ...formData, tipoContribuyente: e.target.value })}
                  style={inputModal}
                >
                  <option value="">-- Seleccionar --</option>
                  <option value="IVA Responsable Inscripto">IVA Responsable Inscripto</option>
                  <option value="IVA Responsable No Inscripto">IVA Responsable No Inscripto</option>
                  <option value="IVA Exento">IVA Exento</option>
                  <option value="Consumidor Final">Consumidor Final</option>
                  <option value="Responsable Monotributo">Responsable Monotributo</option>
                  <option value="Sujeto No Categorizado">Sujeto No Categorizado</option>
                </select>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={labelStyle}>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  style={inputModal}
                  placeholder="correo@ejemplo.com"
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={labelStyle}>Dirección</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  style={inputModal}
                  placeholder="Dirección completa"
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Notas</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  style={{ ...inputModal, minHeight: '80px', resize: 'vertical' }}
                  placeholder="Notas adicionales..."
                />
              </div>

              {/* Sección de Cuenta Corriente */}
              {!isEcommerceLite && (
                <div style={{ borderTop: '2px solid #dee2e6', paddingTop: '20px', marginTop: '20px', marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '15px', color: '#007bff' }}>
                    📒 Configuración de Cuenta Corriente
                  </h3>

                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.cuentaCorrienteEnabled}
                        onChange={(e) => setFormData({ ...formData, cuentaCorrienteEnabled: e.target.checked })}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '14px', fontWeight: '600' }}>Habilitar cuenta corriente para este cliente</span>
                    </label>
                  </div>

                  {formData.cuentaCorrienteEnabled && (
                    <>
                      <div style={{ marginBottom: '15px' }}>
                        <label style={labelStyle}>Límite de Crédito ($) *</label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.creditLimit}
                          onChange={(e) => setFormData({ ...formData, creditLimit: parseFloat(e.target.value) || 0 })}
                          style={inputModal}
                          placeholder="0.00"
                          required={formData.cuentaCorrienteEnabled}
                        />
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                          Monto máximo que el cliente puede deber en cuenta corriente
                        </div>
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={labelStyle}>Días de Plazo de Pago *</label>
                        <input
                          type="number"
                          value={formData.paymentDueDays}
                          onChange={(e) => setFormData({ ...formData, paymentDueDays: parseInt(e.target.value) || 0 })}
                          style={inputModal}
                          placeholder="30"
                          required={formData.cuentaCorrienteEnabled}
                        />
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                          Cantidad de días para el vencimiento de cada venta a crédito
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  style={{ ...btnNav, background: '#6c757d' }}
                  onClick={() => setShowModal(false)}
                >
                  Cancelar
                </button>
                <button type="submit" style={btnGreen}>
                  {editMode ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Estilos
const btnNav = {
  background: '#007bff',
  color: 'white',
  border: 'none',
  padding: '8px 12px',
  borderRadius: '6px',
  cursor: 'pointer',
  fontWeight: 'bold',
  fontSize: '14px',
};

const btnGreen = {
  background: '#28a745',
  color: 'white',
  border: 'none',
  padding: '8px 16px',
  borderRadius: '6px',
  cursor: 'pointer',
  fontWeight: 'bold',
  fontSize: '14px',
};

const btnSmallBlue = {
  background: '#007bff',
  color: 'white',
  border: 'none',
  padding: '4px 8px',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '12px',
};

const btnSmallRed = {
  background: '#dc3545',
  color: 'white',
  border: 'none',
  padding: '4px 8px',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '12px',
};

const inputSearch = {
  padding: '8px 12px',
  borderRadius: '6px',
  border: '1px solid #ccc',
  fontSize: '14px',
  width: '400px',
};

const table = {
  width: '100%',
  borderCollapse: 'collapse',
  backgroundColor: 'white',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
};

const th = {
  padding: '12px',
  textAlign: 'left',
  borderBottom: '2px solid #dee2e6',
};

const td = {
  padding: '12px',
};

const modalOverlay = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const modalContent = {
  backgroundColor: 'white',
  padding: '30px',
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
  maxWidth: '500px',
  width: '90%',
  maxHeight: '90vh',
  overflowY: 'auto',
};

const labelStyle = {
  fontSize: '14px',
  fontWeight: 'bold',
  display: 'block',
  marginBottom: '5px',
};

const inputModal = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: '6px',
  border: '1px solid #ccc',
  fontSize: '14px',
};

export default Customers;
